import { NeedDto, SurveyV2, UserDto } from '@act/shared/data-transfer-objects';
import {
  CollapsibleGoalCard,
  FlexContainer,
  GoalCardActionItems,
  needIsExpiringSoon,
} from '@act/storybook';
import { Box, CircularProgress } from '@mui/material';
import { createRef, RefObject, useEffect, useState } from 'react';
import { goalsTrackingSelectors } from 'redux/slices/goalsTracking.slice';
import { userAppSelectors } from 'redux/slices/usersSlice';

import { useAppNavigation } from 'react-components/app/routing/routes';
import useAddActionDialog from 'react-components/dialog-forms/hooks/use-add-action-dialog';
import useNeedDetailDialog from 'react-components/dialog-forms/hooks/use-need-detail-dialog';
import useTermConsentsDialog from 'react-components/dialog-forms/hooks/use-term-consents-dialog';
import useTextConsentsDialog from 'react-components/dialog-forms/hooks/use-text-consents-dialog';
import useAssignedSurveyDialog from 'react-components/dialog-forms/hooks/useAssignedSurveyDialog';
import useLearnMoreDialog from 'react-components/dialog-forms/hooks/useLearnMoreDialog';
import { needRequests } from 'redux/requests/need.requests';
import { useApiCall } from 'redux/utils/api-hook';
import { Goal } from 'stories/containers/GoalsTracking/types';
import { TrackingProvider } from '../tracking/TrackingContext';
import { AssignedSurveyHospitalizationSummary } from './AssignedSurveyHospitalizationSummary';
import { EmptyGoals } from './EmptyGoals';

interface GoalsProps {
  selectedMember: UserDto;
  view: 'inProgress' | 'completed';
}

const Goals = ({ selectedMember, view }: GoalsProps) => {
  const [_payload, _isLoading, patchNeed] = useApiCall(needRequests.patchNeed);
  const userMap = userAppSelectors.useSelectUsersMap();
  const { toMemberPage } = useAppNavigation();

  const openTextConsentsDialog = useTextConsentsDialog();
  const openTermDialog = useTermConsentsDialog();
  const openLearnMoreDialog = useLearnMoreDialog();
  const openAssignedSurveyDialog = useAssignedSurveyDialog();
  const openAddActionDialog = useAddActionDialog();
  const openNeedDetailsDialog = useNeedDetailDialog();

  function buildActions(goal: Goal) {
    if (goal.goalType === 'consent') {
      if (goal.completed && !goal.consented) {
        return goal.term
          ? [
              {
                label: 'Update consent',
                isPrimary: true,
                onClick: () => openTermDialog({ term: goal.term }),
              },
            ]
          : [];
      } else if (goal.completed && goal.consented) {
        return [];
      } else {
        return [
          {
            label: 'Send consent',
            onClick: () =>
              openTextConsentsDialog({ userId: selectedMember.id }),
          },
          {
            label: 'View consent',
            isPrimary: true,
            onClick: () => openTermDialog({ term: goal.term }),
          },
        ];
      }
    }
    if (goal.goalType === 'need') {
      const need = goal as NeedDto;

      if (goal.completed) {
        return [
          {
            label: 'View details',
            onClick: () => {
              openNeedDetailsDialog({ needId: need.id });
            },
          },
        ];
      }
      const viewNeedAction = {
        label: 'View need',
        onClick: () => {
          toMemberPage(
            selectedMember?.assignedGuideId,
            selectedMember?.id,
            null,
            need?.id.toString(),
            true
          );
        },
      };

      const secondaryAction = needIsExpiringSoon(need)
        ? {
            label: 'Extend time window',
            onClick: () => {
              patchNeed({
                body: { shouldExtend: true },
                params: { needId: need?.id },
              });
            },
          }
        : {
            label: 'Add action',
            onClick: () => openAddActionDialog({ need }),
          };

      return [viewNeedAction, secondaryAction];
    }
    if (goal.goalType === 'survey') {
      const assignedSurvey = goal as SurveyV2.AssignedSurveyDto;
      const list = [];
      const lastHospitalization =
        assignedSurvey?.hospitalizations[
          assignedSurvey?.hospitalizations.length - 1
        ];

      let label = 'Learn more';
      if (assignedSurvey?.hospitalizations.length) {
        if (assignedSurvey.hospitalizations.length > 1) {
          label = 'View latest event';
        } else {
          label = 'View event';
        }
      }

      list.push({
        label,
        onClick: () =>
          openLearnMoreDialog({
            learnMoreContent: assignedSurvey?.survey?.learnMoreContent,
            hospitalization: lastHospitalization,
          }),
      });

      list.push({
        label: assignedSurvey.completed ? 'Edit' : 'Complete now',
        isPrimary: true,
        ddTrackingName: 'complete-survey-button',
        onClick: () =>
          openAssignedSurveyDialog({
            assignedSurveyId: assignedSurvey.id,
            isReviewMode: assignedSurvey.completed,
          }),
      });
      return list;
    }
    return [];
  }

  const goalsLoadingStatus =
    goalsTrackingSelectors.useSelectGoalsTrackingStatus();

  const aggregatedGoals =
    goalsTrackingSelectors.useSelectAggregatedGoalsForMember(
      selectedMember?.id
    );

  const [initialRefs, setInitialRefs] =
    useState<{ [key in string]: RefObject<HTMLDivElement> }>(null);

  useEffect(() => {
    if (aggregatedGoals[view].length > 0) {
      const refs = {};
      aggregatedGoals[view].forEach(goal => {
        refs[`goal-${goal.goalType}-${goal.id}`] = createRef();
      });
      setInitialRefs(refs);
    }
    /**
     * This stringify operation is a workaround for using an array as dependency.
     * It causes a deep comparison to the contents rather than a shallow one.
     */
  }, [JSON.stringify(aggregatedGoals), view]);

  function generateGoalRefId(goal: Goal): string {
    return `goal-${goal.goalType}-${goal.id}`;
  }

  function scrollToGoal(goal: Goal): void {
    const goalRefId = generateGoalRefId(goal);

    initialRefs[goalRefId].current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  return (
    <TrackingProvider contextName="goals-view">
      <Box
        sx={{
          width: '100%',
        }}
      >
        {(goalsLoadingStatus === 'initial' ||
          goalsLoadingStatus === 'loading') && (
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: '100%',
              padding: '64px 0',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {goalsLoadingStatus === 'success' &&
          aggregatedGoals &&
          !aggregatedGoals[view].length && <EmptyGoals mode={view} />}
        {goalsLoadingStatus === 'success' &&
          aggregatedGoals &&
          !!aggregatedGoals[view].length && (
            <FlexContainer
              direction="column"
              padding="16px 0"
              gap={1}
              width="100%"
            >
              {aggregatedGoals[view].map(goal => {
                const actions: GoalCardActionItems[] = buildActions(goal);
                const refId = generateGoalRefId(goal);
                const ref =
                  !!initialRefs && refId in initialRefs
                    ? initialRefs[refId]
                    : null;
                return (
                  <CollapsibleGoalCard
                    ref={ref}
                    key={`${goal.memberId}-${goal.goalType}-${goal.id}`}
                    goal={goal}
                    member={selectedMember}
                    userMap={userMap}
                    defaultClosed={true}
                    actions={actions}
                    summary={
                      goal.hospitalizations?.length > 1 && (
                        <AssignedSurveyHospitalizationSummary goal={goal} />
                      )
                    }
                    onToggleOpen={() => {
                      setTimeout(() => {
                        scrollToGoal(goal);
                      }, 200);
                    }}
                  />
                );
              })}
            </FlexContainer>
          )}
      </Box>
    </TrackingProvider>
  );
};

export default Goals;
